import { InputAmount, MMButton } from '@components'
import { CACHE_KEYS } from '@constants'
import cn from '@integrations/classnames'
import { v4 as uuid } from '@integrations/ids'
import toast from '@integrations/notifications'
import { useContext, useState } from '@integrations/react'
import { PaymentOptionStatuses, useRefundInitiateMutation, useRefundPreviewMutation } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, PaymentOption, TransactionViewPortal } from 'mmfintech-backend-api/types'
import { checkAmountLimits, fixFloat, OtpContext, tr, translateError, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay, LabelValue } from 'mmfintech-portal-commons'
import {
  TransactionActionModalAmount,
  TransactionActionModalInfo,
  TransactionActionModalSurface
} from '../TransactionActionModalSurface'
import RefundPreview from './RefundPreview'

interface Props {
  transaction: TransactionViewPortal
  reload: () => void
}

const STEPS = {
  PREVIEW: 1,
  REFUND: 2
} as const

type Step = (typeof STEPS)[keyof typeof STEPS]

function RefundModal({ transaction, reload }: Props) {
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { amount, id, checkoutDetails, currency, paymentMethod } = transaction || {}
  const { customerName, customerEmail } = checkoutDetails || {}

  const paymentOption: PaymentOption = {
    status: PaymentOptionStatuses.AVAILABLE,
    minAmount: 0,
    maxAmount: amount
  }

  const [refundInit, { error: refundInitError, isLoading: refundInitFetching }] = useRefundInitiateMutation({
    fixedCacheKey: CACHE_KEYS.REFUND_INIT
  })
  const [refundPreview, { error: refundPreviewError, isLoading: refundPreviewFetching, data: refundPreviewData }] =
    useRefundPreviewMutation({
      fixedCacheKey: CACHE_KEYS.REFUND_PREVIEW
    })

  function isFormDataValid() {
    const test = checkAmountLimits(fixFloat(formValues.getValue('amount')), paymentOption)
    if (!test.valid) {
      formValues.setError('amount', translateError(test), test)
      return false
    }

    return true
  }
  const [step, setStep] = useState<Step>(STEPS.PREVIEW)

  const formValues = useFormValues({
    amount: { required: true, validation: 'float', value: transaction?.amount.toString() }
  })

  async function handlePreview(payload: any) {
    try {
      await refundPreview(payload).unwrap()
      setStep(STEPS.REFUND)
    } catch (_err) {}
  }

  async function handleRefundInit(payload: any) {
    setOtpOnSuccess(() => () => {
      toast.success(tr('FRONTEND.TRANSACTIONS.REFUND_SUCCESS', 'Refund initiated'))
      reload()
    })
    try {
      await refundInit(payload).unwrap()
    } catch (_err) {}
  }

  function handleSubmit() {
    if (isFormDataValid()) {
      const payload = {
        refundForeignTransactionId: uuid(),
        originalTransactionId: transaction.id,
        amount: formValues.getValue('amount')
      }

      switch (step) {
        case STEPS.PREVIEW:
          void handlePreview(payload)
          break

        case STEPS.REFUND:
          void handleRefundInit(payload)
          break

        default:
          break
      }
    }
  }

  return (
    <TransactionActionModalSurface className={cn('refund-modal-wrapper')}>
      {step === STEPS.PREVIEW ? (
        <>
          <h3>{tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.TITLE', 'Refund payment')}</h3>

          <TransactionActionModalInfo className={cn('info-container')}>
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.TRANSACTION_ID', 'Transaction ID')}
              value={id}
              colon
              dataTest='transaction-id'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.PAYMENT_METHOD', 'Payment method')}
              value={tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
              colon
              dataTest='payment-method'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.CUSTOMER_NAME', 'Customer name')}
              value={customerName}
              colon
              dataTest='customer-name'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.CUSTOMER_EMAIL', 'Customer email')}
              value={customerEmail}
              colon
              dataTest='customer-email'
            />
          </TransactionActionModalInfo>

          <TransactionActionModalAmount className={cn('amount-container')}>
            <InputAmount
              account={{ currencyCode: currency } as AccountBalanceResponse}
              error={formValues.getError('amount')}
              amount={formValues.getValue('amount')}
              setAmount={(value) => formValues.setValue('amount', value)}
              paymentOption={paymentOption}
              autoFocus
            />
            <div>{currency}</div>
          </TransactionActionModalAmount>
        </>
      ) : null}

      {step === STEPS.REFUND ? (
        <RefundPreview previewData={refundPreviewData} checkoutDetails={checkoutDetails} />
      ) : null}

      <ErrorDisplay error={[refundInitError, refundPreviewError]} />

      <MMButton
        variant='primary'
        caption={
          step === STEPS.PREVIEW
            ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')
            : tr('FRONTEND.TRANSACTIONS.REFUND_MODAL.REFUND_BUTTON', 'Refund')
        }
        onClick={handleSubmit}
        disabled={formValues.getFloat('amount') <= 0}
        loading={refundInitFetching || refundPreviewFetching}
        fullWidth
      />
    </TransactionActionModalSurface>
  )
}

export default RefundModal
